/**
 * useLanguage - React binding for the i18n singleton.
 *
 * Mirrors the useTheme pattern: every mounted component using this hook
 * re-renders when setLanguage() runs -> instant whole-UI switch, no reload.
 * `t` changes identity per language on purpose (safe in dep arrays).
 */

import { useSyncExternalStore, useCallback } from 'react';
import { i18n, LANGUAGES } from './index';
import type { LanguageDefinition, TranslationParams } from './i18n';

export interface UseLanguageResult {
  language: string;
  languages: readonly LanguageDefinition[];
  setLanguage: (code: string) => void;
  t: (key: string, params?: TranslationParams) => string;
}

export function useLanguage(): UseLanguageResult {
  const language = useSyncExternalStore(i18n.subscribe, i18n.getLanguage, i18n.getLanguage);

  const t = useCallback(
    (key: string, params?: TranslationParams) => i18n.t(key, params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  return { language, languages: LANGUAGES, setLanguage: i18n.setLanguage, t };
}
